import React, { useState, useEffect } from 'react';
// Firebase imports are now in apiService.js
// import { initializeApp } from 'firebase/app';
// import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { fetchLatestStockData } from './services/apiService'; // Import the new service
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Divider,
  CircularProgress,
  IconButton,
  CssBaseline,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material';
import { ThemeProvider, createTheme, styled } from '@mui/material/styles';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import WarningIcon from '@mui/icons-material/Warning';

// Firebase config and initialization moved to apiService.js
// const firebaseConfig = {
// apiKey: "AIzaSyC3ak3Ob8Km7cvZ9KJbRxGY9OPDDO2BKvE",
// authDomain: "hkstock-89b58.firebaseapp.com",
// projectId: "hkstock-89b58",
// storageBucket: "hkstock-89b58.firebasestorage.app",
// messagingSenderId: "768875429044",
// appId: "1:768875429044:web:0dd951ac24ff06e3706be1",
// measurementId: "G-5NJJW260BT"
// };

// const app = initializeApp(firebaseConfig);
// const db = getFirestore(app);

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  padding: '12px 16px',
}));

const DisclaimerBanner = styled(Paper)(({ theme }) => ({
  padding: '12px 20px',
  marginBottom: '24px',
  borderRadius: '8px',
  backgroundColor: theme.palette.mode === 'light' ? '#fff8e1' : '#4a3f10',
  border: `1px solid ${theme.palette.mode === 'light' ? '#ffe57f' : '#6d5e0c'}`,
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const ColorModeContext = React.createContext({ toggleColorMode: () => { } });



function getNextBusinessDate(date) {
  const nextDate = new Date(date);
  nextDate.setHours(0, 0, 0, 0); // Reset time component

  // Skip weekends
  while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
    nextDate.setDate(nextDate.getDate() + 1);
  }

  return nextDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}



function RangeTable({ data }) {
  if (!data) return null;

  return (
    <TableContainer component={Paper} sx={{ mb: 3, overflow: 'hidden' }}>
      <Table size="small" sx={{ '& td, & th': { transition: 'background-color 0.2s' } }}>
        <TableHead>
          <TableRow>
            <StyledTableCell>Symbol</StyledTableCell>
            <StyledTableCell align="right">Last Close</StyledTableCell>
            <StyledTableCell align="right">Lower Range</StyledTableCell>
            <StyledTableCell align="right">Upper Range</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(data).map(([symbol, values]) => (
            <TableRow key={symbol}>
              <TableCell component="th" scope="row">{symbol}</TableCell>
              <TableCell align="right">{values.last_close?.toFixed(2)}</TableCell>
              <TableCell align="right">{values.lower_range?.toFixed(2)}</TableCell>
              <TableCell align="right">{values.upper_range?.toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function BiasCard({ symbol, data, rangeData }) {

  return (
    <Card sx={{
      mb: 2,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: (theme) => theme.palette.mode === 'light'
          ? '0 6px 12px rgba(0,0,0,0.1)'
          : '0 6px 12px rgba(0,0,0,0.3)',
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Typography sx={{ mb: 1.5 }}>
          <Box component="span" sx={{ fontWeight: 'bold' }}>Symbol</Box>: {symbol} (Last Close: {rangeData[symbol]?.last_close?.toFixed(2)})
        </Typography>
        <Typography sx={{ mb: 2 }}>
          <Box component="span" sx={{ fontWeight: 'bold' }}>Long Name</Box>: {data.long_name}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Next Day High Bias
            </Typography>
            <Typography>Previous High: {rangeData[symbol]?.last_high?.toFixed(2)}</Typography>
            <Typography>Up: {(data.next_day_bias_up_high * 100).toFixed(2)}%</Typography>
            <Typography>Down: {(data.next_day_bias_down_high * 100).toFixed(2)}%</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Next Day Low Bias
            </Typography>
            <Typography>Previous Low: {rangeData[symbol]?.last_low?.toFixed(2)}</Typography>
            <Typography>Up: {(data.next_day_bias_up_low * 100).toFixed(2)}%</Typography>
            <Typography>Down: {(data.next_day_bias_down_low * 100).toFixed(2)}%</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}


function App() {
  const [loading, setLoading] = useState(true);
  const [latestData, setLatestData] = useState(null);
  const [rangeData, setRangeData] = useState(null);
  const [biasData, setBiasData] = useState(null);

  // Dark mode implementation
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setMode] = useState(prefersDarkMode ? 'dark' : 'light');

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'light' ? '#1565c0' : '#90caf9',
            light: mode === 'light' ? '#42a5f5' : '#bbdefb',
          },
          secondary: {
            main: mode === 'light' ? '#c62828' : '#ef9a9a',
          },
          background: {
            default: mode === 'light' ? '#f8f9fa' : '#121212',
            paper: mode === 'light' ? '#fff' : '#1e1e1e',
          },
          info: {
            main: mode === 'light' ? '#0288d1' : '#29b6f6',
          },
          warning: {
            main: mode === 'light' ? '#ed6c02' : '#ffa726',
            light: mode === 'light' ? '#fff8e1' : '#4a3f10',
          },
        },
        typography: {
          fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
        },
        components: {
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                boxShadow: mode === 'light'
                  ? '0 2px 8px rgba(0,0,0,0.08)'
                  : '0 2px 8px rgba(0,0,0,0.3)',
              },
            },
          },
          MuiTableContainer: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                boxShadow: mode === 'light'
                  ? '0 2px 8px rgba(0,0,0,0.08)'
                  : '0 2px 8px rgba(0,0,0,0.3)',
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                padding: '12px 16px',
              },
              head: {
                fontWeight: 600,
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                '&:last-child td': {
                  borderBottom: 0,
                },
                '&:hover': {
                  backgroundColor: mode === 'light'
                    ? 'rgba(0, 0, 0, 0.04)'
                    : 'rgba(255, 255, 255, 0.04)',
                },
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                margin: '24px 0',
              },
            },
          },
          MuiTypography: {
            styleOverrides: {
              h4: {
                fontWeight: 600,
              },
              h5: {
                fontWeight: 600,
                marginBottom: '16px',
              },
              h6: {
                fontWeight: 500,
              },
            },
          },
        },
      }),
    [mode],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchLatestStockData(); // Use the new service
        setLatestData(data.latest);
        setRangeData(data.rangeData);
        setBiasData(data.biasData);
      } catch (error) {
        console.error("Error fetching data:", error);
        // Optionally, set an error state here to display to the user
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default'
        }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  const nextBusinessDay = latestData?.date
    ? getNextBusinessDate(new Date(latestData.date))
    : '';

  const marketCloseDate = latestData?.date
    ? new Date(latestData.date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    : '';


  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          pb: 4
        }}>
          <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2
              }}>
                <Typography variant="h4" component="h1">
                  Stock Analysis Results
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {latestData?.warning && (
                    <Tooltip title="An error occurred during the last data update.">
                      <WarningIcon color="error" sx={{ mr: 1 }} />
                    </Tooltip>
                  )}
                  <IconButton
                    onClick={colorMode.toggleColorMode}
                    color="inherit"
                    sx={{ ml: 1 }}
                  >
                    {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                  </IconButton>
                </Box>
              </Box>

              {latestData && (
                <>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    {/* Adjust timestamp display based on whether it's a Firebase Timestamp or ISO string from AWS */}
                    Last Updated: {latestData.timestamp?.toDate ? new Date(latestData.timestamp.toDate()).toLocaleString() : (latestData.timestamp ? new Date(latestData.timestamp).toLocaleString() : 'N/A')}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mt: 2, mb: 3 }}>
                    Signals for the next session of {marketCloseDate}
                  </Typography>
                </>
              )}

              <DisclaimerBanner elevation={0}>
                <WarningIcon color="warning" />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  <strong>DISCLAIMER:</strong> The information provided is for reference purposes only and does not constitute investment advice.
                  Past performance is not indicative of future results. Always conduct your own research before making investment decisions.
                </Typography>
              </DisclaimerBanner>


              <Divider sx={{ my: 3 }} />

              <Typography variant="h5" gutterBottom sx={{
                position: 'relative',
                display: 'inline-block',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: '40px',
                  height: '3px',
                  backgroundColor: 'primary.main',
                  borderRadius: '2px'
                }
              }}>
                Price Ranges
              </Typography>
              <RangeTable data={rangeData} />

              <Typography variant="h5" gutterBottom sx={{
                mt: 4,
                position: 'relative',
                display: 'inline-block',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: '40px',
                  height: '3px',
                  backgroundColor: 'primary.main',
                  borderRadius: '2px'
                }
              }}>
                Next Day Bias
              </Typography>
              <Grid container spacing={2}>
                {biasData && Object.entries(biasData).map(([symbol, data]) => (
                  <Grid item xs={12} md={12} key={symbol}>
                    <BiasCard
                      symbol={symbol}
                      data={data}
                      rangeData={rangeData || {}}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>
        </Box>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
